import type { CSSProperties, FunctionComponent, ReactNode } from 'react';
import { useMemo } from 'react';

import { ChipValueFormatter } from '../../../../../formatters/ChipValueFormatter/ChipValueFormatter';
import { ChipTypes } from '../../../../../store/slices/chips/types';
import { useModifiers } from '../../../../../styles/hooks/useModifiers';
import type { ChipViewProps } from '../../types';

import { CasinoChip } from './casinoChip/CasinoChip';
import { FreeChip } from './freeChip/FreeChip';

import styles from './Chip2d.module.css';

interface CustomCSSProps extends CSSProperties {
  '--chipSize': string;
  '--chipBackground': string;
  '--chipRotate': string;
}

export const Chip2d: FunctionComponent<ChipViewProps> = ({
  color,
  locale,
  size = 100,
  weight,
  value,
  postfix = '',
  type = ChipTypes.CasinoChip,
  count,
}) => {
  const mods = useModifiers(
    styles,
    'chip__container',
    {
      [`${type}_weight${weight}`]: true,
    },
    { withBaseClass: true }
  );
  const valueText = useMemo(() => {
    return `${ChipValueFormatter.format(locale, value)}${postfix}`;
  }, [locale, value, postfix]);

  const computeStyles = useMemo((): CustomCSSProps => {
    const styles = {} as CustomCSSProps;
    if (size !== 'inherit' && size !== 'grow') {
      styles['--chipSize'] = `${size}px`;
    }

    return styles;
  }, [size]);

  const renderChipContent = (): ReactNode => {
    if (type === ChipTypes.FreeChip) {
      return <FreeChip valueText={valueText} count={count} computeStyles={computeStyles} />;
    }

    return <CasinoChip valueText={valueText} computeStyles={computeStyles} color={color} />;
  };

  return (
    <span data-testid="chip" className={mods} style={computeStyles}>
      {renderChipContent()}
    </span>
  );
};
